import {useState} from 'react';
import {useLocation, Link} from 'react-router-dom';
import {Grid, Stack, Text} from '@mantine/core';
import {
    PlatformPropertyCard,
    SimplePageContentWrapper,
    TabbedComponent,
    TabComponentTab,
} from '@flexinet/ui-components';

import {Price} from '../types';
import {InformationPropertyCard} from './InformationPropertyCard.tsx';
import {MetadataPropertyCard} from './MetadataPropertyCard.tsx';
import {PriceProviderMappings} from '../components/PriceProviderMappings';
import {IndexPage as SubscriptionItemsPage} from '../../subscription-items/pages/IndexPage.tsx';

const getActiveTabFromHash = (hash: string): string | null => {
    const match = hash.match(/^#tab=(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
};

export const Profile = ({record}: { record: Price }) => {
    const [currentRecord] = useState(record);
    const location = useLocation();
    const activeTab = getActiveTabFromHash(location.hash);

    const recurringProperties = [
        {label: 'Recurring', value: currentRecord.recurringOption, span: 12},
        {label: 'Billing Period', value: currentRecord.billingPeriod, span: 12},
        {label: 'Description', value: currentRecord.description, span: 12},
    ];

    const productProperties = [
        {
            label: 'Product',
            value: currentRecord.productId ? (
                <Link to={`/products/${currentRecord.productId}`} style={{textDecoration: 'none'}}>
                    <Text c="blue" style={{cursor: 'pointer'}}>{currentRecord.product?.name || currentRecord.productId}</Text>
                </Link>
            ) : 'n/a',
            span: 12,
        },
        {label: 'Product ID', value: currentRecord.productId, span: 12},
    ];

    const ratePlanProperties = [
        {label: 'Rate Plan', value: currentRecord.ratePlan?.name || currentRecord.ratePlanId, span: 12},
        {label: 'Rate Plan ID', value: currentRecord.ratePlanId, span: 12},
    ];

    const tabDefinitions: { title: string, component: JSX.Element }[] = [
        {
            title: 'Overview',
            component: (
                <SimplePageContentWrapper>
                    <Stack gap={'xl'}>
                        <PlatformPropertyCard properties={recurringProperties}/>
                    </Stack>
                </SimplePageContentWrapper>
            ),
        },
        {
            title: 'Product',
            component: (
                <SimplePageContentWrapper>
                    <PlatformPropertyCard properties={productProperties}/>
                </SimplePageContentWrapper>
            ),
        },
        {
            title: 'Rate plan',
            component: (
                <SimplePageContentWrapper>
                    <PlatformPropertyCard properties={ratePlanProperties}/>
                </SimplePageContentWrapper>
            ),
        },
        {
            title: 'Subscriptions',
            // FNP-331: cross-subscription SubscriptionItems filtered by this price.
            // Backed by listSubscriptionItemsByPrice query + price-created-index GSI.
            component: (
                <SubscriptionItemsPage priceId={currentRecord.id}/>
            ),
        },
        {
            title: 'Provider Mappings',
            component: <PriceProviderMappings priceId={currentRecord.id}/>,
        },
    ];

    const tabs = tabDefinitions.map(tab => ({
        ...tab,
        active: activeTab ? tab.title === activeTab : undefined,
    })) as TabComponentTab[];

    return (
        <Grid>
            <Grid.Col span={{base: 12, lg: 4}}>
                <Stack gap={'xl'}>
                    <InformationPropertyCard record={currentRecord}/>
                    <MetadataPropertyCard record={currentRecord}/>
                </Stack>
            </Grid.Col>
            <Grid.Col span={{base: 12, lg: 8}}>
                <TabbedComponent key={activeTab ?? 'default'} tabs={tabs}/>
            </Grid.Col>
        </Grid>
    );
};
